import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Delete, Calculator } from 'lucide-react';

interface VirtualKeyboardProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  className?: string;
  placeholder?: string;
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  value,
  onChange,
  onClear,
  className = '',
  placeholder = "Enter your answer"
}) => {
  const [showKeyboard, setShowKeyboard] = useState(false);

  const numberKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  const operatorKeys = ['+', '-', '×', '÷', '=', '.', '(', ')', '%'];
  const specialKeys = ['π', 'e', '√', '^', '∞', '±', '°', '∠', '∆'];

  const handleKeyPress = (key: string) => {
    let newValue = value;

    switch (key) {
      case 'BACKSPACE':
        newValue = value.slice(0, -1);
        break;
      case 'CLEAR':
        newValue = '';
        onClear();
        return;
      case '×':
        newValue = value + '*';
        break;
      case '÷':
        newValue = value + '/';
        break;
      case '√':
        newValue = value + 'sqrt(';
        break;
      case '^':
        newValue = value + '^';
        break;
      case 'π':
        newValue = value + 'π';
        break;
      default:
        newValue = value + key;
    }

    onChange(newValue);
  };

  const toggleKeyboard = () => {
    setShowKeyboard(!showKeyboard);
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Input Display */}
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 pr-16"
          placeholder={placeholder}
          readOnly={showKeyboard}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleKeyboard}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs"
        >
          <Calculator className="h-4 w-4 mr-1" />
          {showKeyboard ? 'Hide' : 'Keypad'}
        </Button>
      </div>

      {/* Virtual Keyboard */}
      {showKeyboard && (
        <Card className="mt-3 p-4 bg-gray-50 border-2 border-blue-200">
          <div className="space-y-3">
            {/* Numbers Row */}
            <div className="grid grid-cols-5 gap-2">
              {numberKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-xl font-semibold hover:bg-blue-100"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Operators Row */}
            <div className="grid grid-cols-5 gap-2">
              {operatorKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-xl font-semibold hover:bg-green-100 text-green-700"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Special Characters Row */}
            <div className="grid grid-cols-5 gap-2">
              {specialKeys.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => handleKeyPress(key)}
                  className="h-12 text-lg font-semibold hover:bg-purple-100 text-purple-700"
                >
                  {key}
                </Button>
              ))}
            </div>

            {/* Action Buttons Row */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeyPress('BACKSPACE')}
                className="h-12 hover:bg-red-100 text-red-600"
              >
                <Delete className="h-4 w-4 mr-2" />
                Backspace
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleKeyPress('CLEAR')}
                className="h-12 hover:bg-red-100 text-red-600"
              >
                Clear All
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={toggleKeyboard}
                className="h-12 bg-blue-600 hover:bg-blue-700"
              >
                Done
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-3 text-xs text-gray-600 text-center">
            <p><strong>Note:</strong> Use the virtual keypad for numeric input to ensure accuracy</p>
            <p>You can also type directly in the input field above</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default VirtualKeyboard;
